import { useMemo, useRef } from 'react'
import { CampaignFormHandle } from 'components/AddCampaignModal/CreateCampaign/Types'
import dayjs from 'dayjs'
import { useAddCampaignMachine } from 'components/AddCampaignModal/useAddCampaignMachine'
import { BrandPlanStrategyType, Website } from 'types'
import { SingleCampaignProps } from 'components/BatchCreateCampaignsModal/SingleCampaign/Types'
import { AddCampaignFlow } from 'components/AddCampaignModal/AddCampaignFlow/AddCampaignFlow'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { compact } from 'lodash'

dayjs.extend(isSameOrBefore)

export const SingleCampaign = ({ brandPlan, startDate, onExit }: SingleCampaignProps) => {
  let validStartDate = dayjs(startDate)

  if (validStartDate.isSameOrBefore(dayjs(), 'day')) {
    validStartDate = dayjs().startOf('day').add(1, 'day')
  }

  const campaignFormRef = useRef<CampaignFormHandle | null>(null)

  const daysBeforeReview = useMemo(() => {
    const daysBeforeReview = brandPlan.days_before_review
    const start = dayjs(validStartDate).startOf('day')
    const maxDays = start.diff(dayjs().add(1, 'day').startOf('day'), 'day')

    return Math.max(Math.min(maxDays, daysBeforeReview), 0)
  }, [brandPlan.days_before_review, validStartDate])

  const [current, send] = useAddCampaignMachine(
    { brandPlanId: brandPlan.id },
    {
      actions: {
        close: onExit,
      },
      context: {
        start_date: validStartDate.format('YYYY-MM-DD'),
        length: 1,
        topic: '',
        week_days: brandPlan.week_days,
        daily_frequency: brandPlan.daily_frequency,
        days_before_review: daysBeforeReview,
        instagram_contents: brandPlan.instagram_contents,
        facebook_contents: brandPlan.facebook_contents,
        linkedin_contents: brandPlan.linkedin_contents,
        twitter_contents: brandPlan.twitter_contents,
        email_contents: brandPlan.email_contents,
        seo_contents: brandPlan.seo_contents,
        tiktok_contents: brandPlan.tiktok_contents,
        youtube_contents: brandPlan.youtube_contents,
        tactics: brandPlan.tactics,
        video_crosspost_contents: brandPlan.video_crosspost_contents,
        crosspost_contents: brandPlan.crosspost_contents,
        crosspost_frequency: ['always', 'never'].includes(brandPlan.crosspost_frequency)
          ? brandPlan.crosspost_frequency
          : 'always',
        cover_image: '',
        audience: '',
        goal: '',
        anchor_content: brandPlan.strategy.anchor_content,
        sources: compact([brandPlan.strategy.anchor_content as Website]),
        strategy: {
          type: BrandPlanStrategyType.Crosspost,
          theme: '',
          themes: [],
          call_to_action: '',
          target_link: brandPlan.strategy.target_link,
          anchor_content_id: brandPlan.strategy.anchor_content?.id,
          anchor_content_type: brandPlan.strategy.anchor_content?.type,
        },
        transient: {
          abortController: new AbortController(),
        },
      },
    },
  )

  return <AddCampaignFlow current={current} send={send} campaignFormRef={campaignFormRef} />
}
